import {
  createElementClickedPayload,
  createProductClickedPayload,
  createProductLoadedPayload,
  createProductViewedPayload,
  type EventContext,
} from '../../utils/createEventingPayloadsDE';
import { createResultsEventContext } from '../../utils/createResultsEventContext';
import tagularEvent from '../../utils/tagularEvent';
import { uuid } from '../../shared/utils';
import type { Result } from '../../types/result';

/**
 * `location` stamped on the product lifecycle events emitted from the
 * side-by-side comparison modal so Cohesion can distinguish a
 * `ProductLoaded` / `ProductViewed` raised inside the modal from the
 * same program's results-page card events.
 */
export const COMPARISON_MODAL_LOCATION = 'dexp-comparison-modal';

export interface ComparisonProductEventArgs {
  /** The compared program the event describes. */
  readonly result: Result;
  /**
   * Inherited flow event context (correlation IDs, dcs, formName …)
   * read from `useFlowTrackingEvents`. When `undefined` — the modal is
   * mounted outside a `DynamicExperienceProvider` (legacy render paths /
   * unit tests) — the emitters no-op so the modal renders without a
   * flow context to beam into.
   */
  readonly eventContext: EventContext | undefined;
  /** 1-based column position of the program in the modal. */
  readonly position: number;
  /** Drives `actionOutcome` (`application` vs `explore`). */
  readonly isProgramSubmitted?: boolean;
  /**
   * DSL result-step config id. When supplied (DSL result page) the
   * product payloads correlate to the published config; omitted on the
   * imperative path → payload unchanged.
   */
  readonly dslConfigId?: string;
  /**
   * Published config version. Passed through when it is an integer of
   * at least 1.
   */
  readonly dslConfigVersion?: number;
}

/**
 * Build the per-program product event context shared by the modal's
 * `ProductLoaded` (on open) and `ProductViewed` (on column view)
 * emitters. Mirrors the imperative `Card` mapping
 * (`createResultsEventContext`) so a compared program emits the same
 * product identity it carried on the results-page card — only the
 * `location` differs.
 */
function buildComparisonProductContext({
  result,
  eventContext,
  position,
  isProgramSubmitted,
  dslConfigId,
  dslConfigVersion,
}: ComparisonProductEventArgs): EventContext {
  return createResultsEventContext({
    eventContext: eventContext as EventContext,
    listId: result.listId,
    presets: {
      degree: result.degreeSlug,
      category: result.categorySlug,
      subject: result.subjectSlug,
    },
    location: COMPARISON_MODAL_LOCATION,
    viewCorrelationId: result.viewCorrelationId,
    productClickedCorrelationId: result.productClickedCorrelationId,
    brand: result.schoolSlug,
    position,
    capId: result.capId,
    programId: result.programId,
    outboundUrl: result.applicationCtaURL,
    isProgramSubmitted,
    dslConfigId,
    dslConfigVersion,
  }) as EventContext;
}

/**
 * Fire `ProductLoaded` for a compared program when the modal opens —
 * parity with the imperative `Card`, which emits `ProductLoaded` on
 * mount independent of visibility. No-op without a flow `eventContext`.
 */
export function emitComparisonProductLoaded(
  args: ComparisonProductEventArgs,
): void {
  if (!args.eventContext) return;
  const ctx = buildComparisonProductContext(args);
  const { name, payload } = createProductLoadedPayload(ctx);
  tagularEvent(name, payload);
}

/**
 * Fire `ProductViewed` for a compared program when its column becomes
 * visible in the modal (driven by `ComparisonTable`'s `onProductViewed`
 * fire-once-per-column callback). No-op without a flow `eventContext`.
 */
export function emitComparisonProductViewed(
  args: ComparisonProductEventArgs,
): void {
  if (!args.eventContext) return;
  const ctx = buildComparisonProductContext(args);
  const { name, payload } = createProductViewedPayload(ctx);
  tagularEvent(name, payload);
}

/**
 * Fire `ProductClicked` for a compared program when the user clicks its
 * CTA (`Explore Program` / `Next Steps`) inside the comparison modal —
 * parity with the imperative `Card`, which emits `ProductClicked` from
 * its CTA handler. Threads the same product identity the program carried
 * on the results-page card (only the `location` differs,
 * {@link COMPARISON_MODAL_LOCATION}); `actionOutcome` resolves to
 * `application` vs `explore` from `isProgramSubmitted`. No-op without a
 * flow `eventContext`.
 */
export function emitComparisonProductClicked(
  args: ComparisonProductEventArgs,
): void {
  if (!args.eventContext) return;
  const ctx = buildComparisonProductContext(args);
  const { name, payload } = createProductClickedPayload(ctx);
  tagularEvent(name, payload);
}

/**
 * `webElement.elementType` for the per-program remove (X) control on a
 * comparison card. Distinct from the product `location`
 * (`COMPARISON_MODAL_LOCATION`) so analytics owners can isolate the
 * remove interaction.
 */
export const COMPARISON_CLOSE_ELEMENT_TYPE = 'dexp-comparison-popup-close';

/** `webElement.name` for the per-program remove (X) control. */
export const COMPARISON_CLOSE_NAME = 'dexp-comparison-popup-close-button';

/** `webElement.location` for the remove (X) beam. */
export const COMPARISON_POPUP_LOCATION = 'dexp-comparison-popup';

/** `webElement.text` label for the remove (X) control. */
export const COMPARISON_CLOSE_TEXT = 'X';

export interface ComparisonProgramRemovedEventArgs {
  /**
   * Inherited flow event context (correlation IDs) read from
   * `useFlowTrackingEvents`. The remove `correlationId` falls back to
   * `formViewedCorrelationId` (parity with every other DE
   * `ElementClicked`) and `viewCorrelationId` threads the flow view
   * correlation. When `undefined` — the modal is mounted outside a
   * `DynamicExperienceProvider` (legacy render paths / unit tests) — a
   * fresh uuid backs `correlationId` so the beam is never emitted with an
   * undefined correlation.
   */
  readonly eventContext: EventContext | undefined;
  /**
   * DSL result-step config id. When supplied (DSL result page) the remove
   * beam correlates to the published config via a `customDimensions`
   * entry; omitted on the imperative path → payload unchanged.
   */
  readonly dslConfigId?: string;
}

/**
 * Read the Cohesion `webContext` off the global the Cohesion snippet
 * populates so the remove beam carries page / session context. Mirrors
 * the compare-checkbox `ElementClicked` emitter (`compareAnalytics.ts`).
 * Returns `undefined` server-side or before Cohesion initializes so the
 * beam degrades gracefully instead of attaching an empty block.
 */
function getCohesionWebContext(): Record<string, unknown> | undefined {
  if (typeof window === 'undefined') return undefined;
  return window._Cohesion?.webContext as Record<string, unknown> | undefined;
}

/**
 * Fire an `ElementClicked` beam when the user removes a program from the
 * comparison by clicking the remove (X) control on its card. Routes
 * through the canonical {@link createElementClickedPayload} builder so
 * the wire payload matches every other DE `ElementClicked` (top-level
 * `correlationId` / `viewCorrelationId`, a `webElement` block, optional
 * `customDimensions`) and manually attaches the Cohesion `webContext`
 * (parity with the compare-checkbox beam).
 */
export function emitComparisonProgramRemoved({
  eventContext,
  dslConfigId,
}: ComparisonProgramRemovedEventArgs): void {
  const { name, payload } = createElementClickedPayload({
    correlationId: eventContext?.formViewedCorrelationId ?? uuid(),
    viewCorrelationId: eventContext?.viewCorrelationId,
    elementType: COMPARISON_CLOSE_ELEMENT_TYPE,
    name: COMPARISON_CLOSE_NAME,
    location: COMPARISON_POPUP_LOCATION,
    text: COMPARISON_CLOSE_TEXT,
    ...(dslConfigId
      ? { customDimensions: [{ key: 'dslConfigId', value: dslConfigId }] }
      : {}),
  });
  const webContext = getCohesionWebContext();
  tagularEvent(name, webContext ? { ...payload, webContext } : payload);
}
